import { useState, useEffect } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useAuth } from '@/lib/contexts/auth-context';
import FormVisibility, { FORM_VISIBILITY_ID } from '../forms/FormVisibility';
import { useTranslation } from 'react-i18next';

/**
 * Convertit la réponse getCollaborators en format selected_companies pour PermissionsSelector.
 */
function buildInitialSelectedCompanies(data, ownCompanyId) {
    const result = [];

    // Si all_users_access → propre entreprise cochée sans user_ids (= tous)
    if (data.all_users_access) {
        result.push({ company_id: ownCompanyId });
    } else if (data.selected_users?.length > 0) {
        // Des collaborateurs spécifiques de l'entreprise sont sélectionnés
        result.push({ company_id: ownCompanyId, user_ids: [...data.selected_users] });
    }

    // Sous-comptes sélectionnés
    for (const companyId of data.selected_companies ?? []) {
        result.push({ company_id: companyId });
    }

    return result;
}

/**
 * Convertit le format selected_companies (PermissionsSelector) vers le payload updateCollaborators.
 */
function buildUpdatePayload(selectedCompanies, ownCompanyId) {
    const ownEntry = selectedCompanies.find((p) => p.company_id === ownCompanyId);
    const otherCompanies = selectedCompanies.filter((p) => p.company_id !== ownCompanyId).map((p) => p.company_id);

    if (!ownEntry) {
        return { all_users: false, users: [], companies: otherCompanies };
    }

    // Pas de user_ids = tous les utilisateurs
    const allUsers = !ownEntry.user_ids;
    return {
        all_users: allUsers,
        users: allUsers ? [] : ownEntry.user_ids,
        companies: otherCompanies,
    };
}

export default function SheetVisibility({ open, onClose, procedureId, procedureName = '', onSuccess }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [initialSelectedCompanies, setInitialSelectedCompanies] = useState(null);

    const companyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;

    // Charger les données de visibilité actuelles à l'ouverture
    useEffect(() => {
        if (!open || !procedureId || !companyId) return;
        setIsLoading(true);
        horodatyApi.procedureTemplates
            .getCollaborators(procedureId)
            .then((res) => {
                setInitialSelectedCompanies(buildInitialSelectedCompanies(res.data, companyId));
            })
            .catch(() => {
                toast.error(t('procedures:erreurLorsDuChargementDeLaVisibilite'));
                onClose();
            })
            .finally(() => setIsLoading(false));
    }, [open, procedureId, companyId]);

    const handleSubmit = async (selectedCompanies) => {
        setIsSaving(true);
        try {
            const payload = buildUpdatePayload(selectedCompanies, companyId);
            await horodatyApi.procedureTemplates.updateCollaborators(procedureId, payload);
            toast.success(t('procedures:visibiliteMiseAJour'));
            onSuccess?.();
            onClose();
        } catch (err) {
            if (err.response?.status === 409) {
                toast.error(t('procedures:modificationNonAutoriseeDroitsInsuffisants'));
            } else {
                toast.error(t('procedures:erreurLorsDeLaSauvegarde'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <SheetContent showCloseButton={false} className="w-[min(90vw,480px)] flex flex-col p-0 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h2 className="text-[18px] text-foreground leading-tight pr-4">{t('procedures:visibilite')} {procedureName}</h2>
                    <Button variant="soft" color="neutral" size="xmd" onClick={onClose} className="shrink-0">
                        <X className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                </div>

                {/* Form (scrollable) */}
                {isLoading || !initialSelectedCompanies ? (
                    <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <FormVisibility
                        key={procedureId}
                        initialSelectedCompanies={initialSelectedCompanies}
                        onSubmit={handleSubmit}
                    />
                )}

                {/* Footer */}
                <div className="border-t border-border p-5 shrink-0">
                    <div className="flex gap-3">
                        <Button
                            variant="soft"
                            color="neutral"
                            size="cta"
                            type="button"
                            className="flex-1"
                            onClick={onClose}
                        >
                            {t('procedures:annuler')}
                        </Button>
                        <Button
                            variant="solid"
                            color="success"
                            size="cta"
                            type="submit"
                            className="flex-1"
                            form={FORM_VISIBILITY_ID}
                            disabled={isLoading || isSaving || !initialSelectedCompanies}
                        >
                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('procedures:enregistrer')}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
